import OpenInNew from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useMarkNotificationAsViewed, useNotificationFeedQuery } from 'hooks/useNotificationFeed';
import { userBffQueries } from 'hooks/userBffQueries';
import { QueryProviderV5 } from 'providers/QueryProviderV5';
import { navigate } from 'utils/navigate';
import NotificationEventRow from './NotificationEventRow';

const NotificationsFeedContent: React.FC<{ onClose?: VoidFunction }> = ({ onClose }) => {
  const { data: currentUser } = useQuery(userBffQueries.currentUser);
  const userId = currentUser?.id ?? null;
  const { data: notifications, isLoading } = useNotificationFeedQuery(userId, { limit: 6 });
  const { markNotificationAsViewed } = useMarkNotificationAsViewed(userId);

  const { t } = useTranslation();

  const notificationItems = notifications?.items ?? [];

  return (
    <>
      <Stack>
        <Box sx={{ padding: (theme) => theme.spacing(2, 2, 1, 2) }}>
          <Typography variant="h6">{t('notifications_feed.title')}</Typography>
        </Box>

        <Stack sx={{ padding: (theme) => theme.spacing(2, 0.5, 0, 0.5) }}>
          {isLoading ? (
            <Stack height="520px" justifyContent="center" alignItems="center">
              <CircularProgress size={24} />
            </Stack>
          ) : (
            notificationItems.map((event) => (
              <NotificationEventRow
                key={event.notificationId}
                event={event}
                handleMarkAsViewed={markNotificationAsViewed}
                onClose={onClose}
              />
            ))
          )}
        </Stack>
      </Stack>
      <Divider />
      <Stack sx={{ padding: '16px 24px' }} justifyContent="center" alignItems="center">
        <Button
          variant="text"
          color="primary"
          startIcon={<OpenInNew />}
          disabled={isLoading}
          size="small"
          fullWidth
          onClick={() => navigate('/notifications-feed')}
        >
          {t('notifications_feed.view_more_button')}
        </Button>
      </Stack>
    </>
  );
};

export const NotificationsFeed: React.FC<{ onClose?: VoidFunction }> = ({ onClose }) => {
  return (
    <QueryProviderV5>
      <NotificationsFeedContent onClose={onClose} />
    </QueryProviderV5>
  );
};
